import React, {useState, useEffect, useRef} from 'react';
import {postToHub} from "../../actions/networkActions";
import useGet from "../../hooks/useGet";
import Checkbox from "../Checkbox";
import InfoBox from "../passengerInformation/InfoBox";
import {isDevelopmentMode} from "../../utils/configUtils";
import Modal from "../Modal";
import ActionButton from "../ActionButton";
import {convertYMDtoDMY} from "../../utils/dateTimeUtils";
import { Trans, useLingui } from '@lingui/react/macro';
import { navigate } from '../../utils/navigationUtils';
import { pollPaymentWebhookStatus } from "../../utils/adyenUtils";
import * as Sentry from "@sentry/react";

// loaded on demand, so the Adyen SDK and its CSS stay out of the main bundle that every page downloads
const AdyenDropin = React.lazy(() => import("../payment/AdyenDropin"));

function CardRegistration(props) {

    const [iframeUrl, setIframeUrl] = useState("");
    const [pageState, setPageState] = useState("loading");
    const [trigger, setTrigger] = useState(0);
    const [noCardReason, setNoCardReason] = useState("");
    const [submitInProgress, setSubmitInProgress] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [paymentProvider, setPaymentProvider] = useState("");
    const [adyenSession, setAdyenSession] = useState(null);
    const [adyenTransactionId, setAdyenTransactionId] = useState(null);
    const [adyenErrorKind, setAdyenErrorKind] = useState(null);
    // aborted when the guest leaves the page, so a poll that is still running stops and reports nothing
    const pollAbort = useRef(null);
    
    const {t} = useLingui();

    useEffect(() => {
        const controller = new AbortController();
        pollAbort.current = controller;
        return () => controller.abort();
    }, []);


    useEffect(() => {
        const getRegistrationData = async () => {
            setPageState("loading");

            let url = "ccregistration/redirecturl";
            url += `?replaceExisting=${props.replaceExisting ? "true" : "false"}`;

            let response = await postToHub(url, {passengerId: props.passengerId});

            if (!response || response.error) {
                setPageState("error");
                return;
            }

            const provider = (response.paymentProvider || "").toUpperCase();

            if (provider === "ADYEN") {
                const sessionId = response.id;
                const sessionData = response.sessionData;
                const clientKey = response.clientKey;
                const transactionId = response.transactionId;

                // without a clientKey the Drop-in can't start, and without a transactionId the payment can't be confirmed
                if (sessionId && sessionData && clientKey && transactionId) {
                    setPaymentProvider("ADYEN");
                    setAdyenSession({ id: sessionId, sessionData, clientKey });
                    setAdyenTransactionId(transactionId);
                    setPageState("ccentry");
                } else {
                    const missing = ["id", "sessionData", "clientKey", "transactionId"].filter(key => !response[key]);
                    Sentry.captureMessage("Adyen session from the hub is incomplete", {
                        level: "error",
                        fingerprint: ["adyen-incomplete-session"],
                        tags: {
                            "payment.passengerId": props.passengerId,
                            "payment.missing": missing.join(","),
                        },
                    });
                    setAdyenErrorKind("config");
                    setPageState("error");
                }
            } else {
                // PXP, PAPAGENA, or legacy default
                setPaymentProvider(provider || "PXP");
                setPageState("ccentry");
                setIframeUrl(response.redirectUrl);
            }
        };

        getRegistrationData();
    }, [props.passengerId, props.replaceExisting, trigger]);

    // the first round of polling offers "Try again" when it times out; the final round (the retry) gives up and reports it
    const pollStatus = async ({ final }) => {
        setPageState("polling");

        const signal = pollAbort.current.signal;
        // Poll backend tokenization-status endpoint for 15 seconds (1 second interval)
        const pollResult = await pollPaymentWebhookStatus({
            transactionId: adyenTransactionId,
            timeoutSeconds: 15,
            intervalMs: 1000,
            passengerId: props.passengerId,
            signal
        });

        if (signal.aborted) return;

        if (pollResult.isAuthorised) {
            setPageState("success");
        } else if (pollResult.isExpired) {
            setPageState("expireError");
        } else if (pollResult.timedOut && !final) {
            // First poll timed out. Show fake retry state.
            setPageState("pending");
        } else if (pollResult.timedOut) {
            // Second poll also timed out. No more retries.
            setPageState("pendingFinal");

            Sentry.captureMessage("Adyen payment webhook poll timed out after second retry", {
                level: "warning",
                fingerprint: ["adyen-payment-webhook-poll-timeout"],
                tags: {
                    "payment.transactionId": adyenTransactionId,
                    "payment.passengerId": props.passengerId,
                    "payment.failedRequests": pollResult.failedRequests, //0 will mean slow webhook, 15 will mean hub errors
                },
            });
        } else {
            setPageState("error");
        }
    };

    const handleAdyenPaymentCompleted = async (result) => {

        const resultCode = (result?.resultCode || "").toUpperCase();

        if (!["AUTHORISED", "RECEIVED", "PENDING"].includes(resultCode)) {
            setPageState("error");
            return;
        }

        await pollStatus({ final: false });
    };

    const handleAdyenPaymentFailed = () => {
        setPageState("error");
    };

    const handleAdyenError = (kind, error) => {
        // Adyen failures leave no other trace. Network and configuration ones (e.g. the client key's allowed origins) are
        // errors; the rest (card fields failing to load, 3DS2) are warnings. A shopper's cancel isn't reported
        if (error?.name !== "CANCEL") {
            Sentry.captureException(error, {
                level: kind === "generic" ? "warning" : "error",
                fingerprint: [`adyen-dropin-${kind}-error`],
                tags: {
                    "payment.transactionId": adyenTransactionId,
                    "payment.passengerId": props.passengerId,
                },
            });
        }
        setAdyenErrorKind(kind);
        setPageState("error");
    };

    const handleRetryPoll = () => pollStatus({ final: true });

    const handleTryAgain = () => {
        setIframeUrl("");
        setAdyenSession(null);
        setAdyenTransactionId(null);
        setPaymentProvider("");
        setAdyenErrorKind(null);
        setPageState("loading");
        setTrigger(prev => prev + 1);
    };

    async function submit() {
        if(!submitInProgress) {
            const body = {
                passengerId: props.passengerId,
                category: "cardDetails",
                answers: {"cc-1": noCardReason}
            }

            setSubmitInProgress(true);
            const submitUrl = 'precruise/questions/submitAnswers';
            const result = await postToHub(submitUrl, body);
            setSubmitInProgress(false);

            if(result !== "") {
                setSubmitError(t({id: "cc_page_error_submitting_data"}));
                return;
            }
            else {
                navigate(t({id: "/precruise/passengers"}) + "/cc");
            }
        }
    }


    return (
        <div>
            <div className="leading-middle text-lg text-grey-darker">
                {noCardReason === "" && pageState === "ccentry" && <Guidelines cruiseEnd={convertYMDtoDMY(props.cardInfo.cardMaxValidityDate)}/>}
                {noCardReason === "payWithCash" && <PayWithCashText/>}
                {noCardReason === "notWant" && <NotWantText/>}
            </div>

            {noCardReason === "" &&
            <InfoBox
                classes="mt-6 md:inline-block"
                infoText= {t({id: "cc_info_text"})}
            />}

            {/* TMP only, TODO remove */}
            {isDevelopmentMode() && noCardReason === "" &&
            <div className="my-4">
                State: {pageState} | Provider: {paymentProvider}<br/>
                TODO remove after testing<br/>
                4111111111111517 - demo card
            </div>
            }

            {pageState === "loading" && (
                <div className="flex flex-col items-center justify-center p-8 my-6">
                    <div className="w-10 h-10 border-4 border-cta border-t-transparent rounded-full animate-spin mb-3"></div>
                    <p className="text-grey-darker"><span translate="no"><Trans id="loading">loading</Trans>...</span></p>
                </div>
            )}

            {pageState === "polling" && (
                <div className="flex flex-col items-center justify-center p-8 bg-white rounded shadow-sm border border-grey-light my-6">
                    <div className="w-10 h-10 border-4 border-cta border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-grey-darker font-medium text-lg">
                        <Trans id="cc_verifying_card">Verifying your card details...</Trans>
                    </p>
                    <p className="text-sm text-grey-dark mt-2 text-center max-w-md">
                        <Trans id="cc_verifying_please_wait">Please wait while we confirm your card registration with your bank. Do not refresh this page.</Trans>
                    </p>
                </div>
            )}

            {pageState === "pending" && (
                <div className="mt-6">
                    <div className="text-lg text-grey-darker">
                        <Trans id="cc_verification_delayed">We're still confirming your card registration. Please click below to check the status again.</Trans>
                    </div>
                    <div className="mt-4">
                        <button
                            className="w-mobile action-button action-button-enabled"
                            onClick={handleRetryPoll}
                        >
                            <Trans id="try_again">Try again</Trans>
                        </button>
                    </div>
                </div>
            )}

            {pageState === "pendingFinal" && (
                <div className="mt-6 text-lg text-grey-darker">
                    <Trans id="cc_verification_pending">Your card registration is still being processed. Please check back in a few minutes - there's no need to register again.</Trans>
                </div>
            )}

            {pageState === "error" &&
                <div>
                    <div className="mt-6 text-lg text-grey-darker">
                        {adyenErrorKind === "network"
                            ? <Trans id="cc_payment_service_network_error">There was a problem contacting the payment service. Please try again.</Trans>
                            : adyenErrorKind === "config"
                            ? <Trans id="cc_payment_service_config_error">Payment service configuration error. Please contact customer support.</Trans>
                            : <Trans id="cc_registering_issue_text">
                                There has been an issue with registering your card, please check all details are correct and try again
                              </Trans>
                        }
                    </div>
                    <div className="mt-4">
                        <button 
                            className="w-mobile action-button action-button-enabled"
                            onClick={handleTryAgain}
                        >
                            <Trans id="try_again">Try again</Trans>
                        </button>
                    </div>
                </div>
            }

            {pageState === "expireError" &&
            <div>
                <div className="mt-6 text-lg text-grey-darker">
                    <Trans id="cc_expiry_text">
                        Unfortunately the card you have provided expires before the end of your cruise. Please check details are correct or provide a new card and try again
                    </Trans>
                </div>
                <div className="mt-4">
                    <button className="w-mobile action-button action-button-enabled" onClick={handleTryAgain}>
                        <Trans id="try_again">Try again</Trans>
                    </button>
                </div>
            </div>
            }

            {pageState === "success" &&
                <CCRouting passengerId={props.passengerId} />
            }

            {/* PXP / PAPAGENA flow: renders iframe */}
            {pageState === "ccentry" && paymentProvider !== "ADYEN" && iframeUrl && noCardReason === "" &&
                <CCForm
                    url={iframeUrl}
                    setPageState={setPageState}
                />
            }

            {/* ADYEN's DropIn */}
            {pageState === "ccentry" && paymentProvider === "ADYEN" && adyenSession && noCardReason === "" &&
                <div className="my-4">
                    {/* fallback stays null: loading text here breaks under browser page translation */}
                    <React.Suspense fallback={null}>
                        <AdyenDropin
                            session={adyenSession}
                            onPaymentCompleted={handleAdyenPaymentCompleted}
                            onPaymentFailed={handleAdyenPaymentFailed}
                            onError={handleAdyenError}
                        />
                    </React.Suspense>
                </div>
            }

            {pageState === "ccentry" && noCardReason === "" && ((paymentProvider === "ADYEN" && adyenSession) || (paymentProvider !== "ADYEN" && iframeUrl)) &&
                <NoCardOptions
                    replaceExisting={props.replaceExisting}
                    setNoCardReason={setNoCardReason}
                />
            }

            {noCardReason !== "" &&
            <ActionButton
                onClick={submit}
                disabled={submitInProgress}
                classes="mb-8 mt-4 xl:mb-12 w-mobile"
                buttonText={t({id: "next"})}/>
            }

            {submitError &&
            <div className="mt-4 text-red">
                {submitError}
            </div>
            }
        </div>
    );
}

export default CardRegistration;

function Guidelines(props) {
    return (
        <Trans id="cc_guideline_text">
            <p className="mb-2">
                Here you have the opportunity to register a credit or debit card ahead of your sailing. A cashless system is in operation for all your onboard purchases and by registering your card before you travel, you’ll save time having to complete the registration process at the port. If you complete this step now, your account will be open and ready for you, as soon as you step onboard.
            </p>
            <p className="mb-2">
                Please ensure that the card you register does not expires before the end of your sailing, which is {props.cruiseEnd}
            </p>
            <p className="mb-2">
                Once you have registered your card, a pre-authorisation charge of £0.01 will show on your account. This will remain on your statement as a pending transaction until the end of your cruise.
            </p>
        </Trans>
    )
}

function PayWithCashText(props) {
    return (
        <p>
            <Trans id="pay_with_cash_text">
                For those guests not wishing to pay by credit or debit card, payment can be made in cash but you must deposit cash at the ship’s Reception, as your onboard account will not be active until you have done so and no purchases can be made onboard.
            </Trans>
        </p>
    )
}

function NotWantText(props) {
    return (
        <p>
            <Trans id="not_want_text">
                Thank you for letting us know that you don’t want to register a card before you travel. This does mean that your online check-in process will remain incomplete and you won’t be able to access your boarding pass. You’ll need to join the Standard Check-in Lane when you arrive at the port and check-in manually with our team.
            </Trans>
        </p>
    )
}

// TODO - this should be made dynamic based on the theme - maybe pulled from env variables
const allowedOrigins = [
    "https://ambassador-digitech-presales.castusdev.co.uk", // AMB test
    "https://pre-travel.ambassadorcruiseline.com", // AMB prod
    "https://cfc-presales.digitech.world", // CFC test
    "https://presales.digitech.world", // CFC test 2 (for upgrade of cfc theme)
    "https://avantdepart.cfc-croisieres.fr", // CFC prod - OLD - TODO remove once migration to new domain is confirmed successful
    "https://avantdepart.ambassadorcroisieres.fr", // CFC prod 2026 and onwards
    "http://localhost:3000", // local dev
];

const acceptedStates = [
    "success",
    "expireError",
    "error"
];

function CCForm(props) {
    const [iframeHeight, setIframeHeight] = useState(600);
    useEffect(() => {
        function handleMessage(e) {
            if(allowedOrigins.includes(e.origin) && acceptedStates.includes(e.data)) {
                props.setPageState(e.data);
            }
        }

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    const iframeRef = React.createRef();

    return (
        <iframe
            className="w-full border overflow-hidden mb-8"
            style={{ height: `${iframeHeight}px` }}
            src={props.url}
            title=""
            ref={iframeRef}
        />
    );
}

function NoCardOptions({ replaceExisting, setNoCardReason }) {
    const [showModal, setShowModal] = useState("");

    function handleNoCard(paxOwnsCard) {
        setNoCardReason(paxOwnsCard ? "notWant" : "payWithCash");
        setShowModal("");
    }

    if (replaceExisting) return null;

    return (
        <div className="mt-8">
            <button
                onClick={() => setShowModal("reasonSelectModal")}
                className="mb-8 xl:mb-12 underline"
            >
                <Trans id="cc_registration_question">Are you unable to register a card or want to pay with cash onboard?</Trans>
            </button>

            {/* Modal for choosing i dont want to / i don't own a card*/}
            <ReasonSelectModal
                showModal={showModal}
                setShowModal={setShowModal}
                handleNoCard={handleNoCard}
            />

            {/* Modal for showing benefits of adding a card*/}
            <ConfirmNoCardModal
                showModal={showModal}
                setShowModal={setShowModal}
                handleNoCard={handleNoCard}
            />
        </div>
    );
}

function CCRouting (props) {
    const statusUrl = "passengers/all";
    const passengers = useGet(statusUrl, []);
    const [selectedPassengers, setSelectedPassengers] = useState([]);
    const {t} = useLingui();

    const togglePassenger = passengerId => {
        setSelectedPassengers(
            selectedPassengers.includes(passengerId) ?
                selectedPassengers.filter(selectedId => selectedId !== passengerId) :
                [...selectedPassengers, passengerId]
        )
    }

    const onSubmit = async () => {
        if(selectedPassengers.length > 0) {
            const payload = {
                sourcePassengerId: props.passengerId,
                targetPassengerIds: selectedPassengers,
            }
            const postUrl = 'ccregistration/assignccdetails';
            const result = await postToHub(postUrl, payload);
            // TODO - handle possible error cases based on result
        }

        navigate(t({id: "/precruise/passengers"}) + "/cc");
    }

    const sourcePassenger = passengers.find(passenger => passenger.id === props.passengerId);

    return (
        <div className="mt-6">
            <div className="text-lg text-grey-darker">
                <Trans id="cc_assign_question">Who do you want to assign the card to?</Trans>
            </div>

            <div>
                {passengers.length > 0 &&
                    <div className="mt-4">
                        <Checkbox
                            checked={true}
                            value={props.passengerId}
                            disabled={true}
                            handleCheckboxChange={() => {}}>
                            {sourcePassenger.firstName} {sourcePassenger.lastName}
                        </Checkbox>
                    </div>
                }
                {passengers.filter(passenger => passenger.id !== props.passengerId).map(passenger =>
                    <div className="my-4" key={passenger.id}>
                    <Checkbox
                        checked={selectedPassengers.includes(passenger.id)}
                              value={passenger.id}
                              handleCheckboxChange={() => togglePassenger(passenger.id)}>
                        {passenger.firstName} {passenger.lastName}
                    </Checkbox>

                    </div>
                )}
            </div>
            <div>
                <button className="action-button action-button-enabled w-mobile" onClick={onSubmit}>
                    <Trans id='submit'>Submit</Trans>
                </button>
            </div>
        </div>
    );
}

function ReasonSelectModal(props) {
    const {t} = useLingui();
    return (
        <Modal
            show={props.showModal === "reasonSelectModal"}
            onClose={() => props.setShowModal("")}
            title={t({id: "continue_without_cc"})}
        >
            <div className="p-4">
                <div className="text-grey-darker text-lg">
                    <p>
                        <Trans id="select_the_reason_text">Please select the reason for not registering a card.</Trans>
                    </p>
                </div>

                <div className="mt-8 md:flex md:justify-between">
                    <ActionButton
                        onClick={() => props.handleNoCard(false)}
                        disabled={false}
                        classes="max-w-xs text-base"
                        buttonText={t({id: "i_want_pay_with_cash"})}/>

                    <ActionButton
                        onClick={() => props.setShowModal("confirmNoCardModal")}
                        disabled={false}
                        classes="mt-12 md:mt-0 max-w-xs text-base"
                        buttonText={t({id: "i_dont_want_to_register_cc"})}/>
                </div>
            </div>
        </Modal>
    )
}

function ConfirmNoCardModal(props) {
    const {t} = useLingui();
    return (
        <Modal
            show={props.showModal === "confirmNoCardModal"}
            onClose={() => props.setShowModal("")}
            title={t({id: "dont_want_to_register_cc"})}>
            <div className="p-4">
                <Trans id="no_cc_registration_info_text">
                    <div className="text-primary-dark text-2xl">
                        <p>
                        Did you know...
                        </p>
                    </div>
                    <div className="text-grey-darker">
                        <div className="text-lg">
                            We've implemented online check-in to make things easier for you.
                        </div>
                        <p className="mt-2 text-lg">
                            Providing your guest information and registering a card before sailing, means your boarding pass will be available and you can join the Fastrack Embarkation Lane when you arrive at the port and you’ll be on the ship in no time. Otherwise you’ll need to join the Standard Lane and check-in manually with our team.
                        </p>
                    </div>
                </Trans>

                <div className="mt-6 md:flex md:justify-between">
                    <ActionButton
                        onClick={() => props.setShowModal("")}
                        disabled={false}
                        classes="mt-4 mx-4 md:mt-0 text-base"
                        buttonText={t({id: "i_want_to_register_cc"})}/>

                    <ActionButton
                        onClick={() => props.handleNoCard(true)}
                        disabled={false}
                        classes="mt-4 mx-4 md:mt-0 text-base"
                        buttonText={t({id: "i_dont_want_to_register_cc"})}/>
                </div>
            </div>
        </Modal>
    )
}

